<script setup>
import { ref, watch, getCurrentInstance } from 'vue';
import { router } from '@inertiajs/vue3';
import MainLayout from '@/layout/main/MainLayout.vue';
import Button from 'primevue/button';
import Card from 'primevue/card';
import InputText from 'primevue/inputtext';
import ColorPicker from 'primevue/colorpicker';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import { useForm } from '@inertiajs/vue3';
import { useLoading } from '@/composables/useLoading';

defineOptions({
    layout: MainLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    title: String,
    project: {
        type: Object,
        required: true
    }
});

// Color state for ColorPicker component
const selectedColor = ref(props.project.color?.replace('#', '') || '3B82F6');

// Form
const form = useForm({
    name: props.project.name,
    color: props.project.color,
});

// Watch for project changes (in case of updates from server)
watch(() => props.project, (newProject) => {
    form.name = newProject.name;
    form.color = newProject.color;
    selectedColor.value = newProject.color?.replace('#', '') || '3B82F6';
}, { deep: true });

// Watch for color changes and update form
const updateColor = (value) => {
    // ColorPicker returns the value without #, so we add it
    form.color = '#' + value;
};

// Functions
const submit = () => {
    showLoading();
    form.put(route('client.projects.update', props.project.id), {
        onSuccess: () => {
            hideLoading();
            toast.add({ 
                severity: 'success', 
                summary: $t('Success'), 
                detail: $t('Project updated successfully'), 
                life: 3000 
            });
        },
        onError: () => {
            hideLoading();
            toast.add({ 
                severity: 'error', 
                summary: $t('Error'), 
                detail: $t('Failed to update project'), 
                life: 3000 
            });
        }
    });
};

const cancel = () => {
    router.get(route('client.projects.index'));
};

const resetForm = () => {
    form.name = props.project.name;
    form.color = props.project.color;
    selectedColor.value = props.project.color?.replace('#', '') || '3B82F6';
    form.clearErrors();
};

const confirmDelete = () => {
    if (confirm($t('Are you sure you want to delete this project? This action cannot be undone.'))) {
        deleteProject();
    }
};

const deleteProject = () => {
    showLoading();
    router.delete(route('client.projects.destroy', props.project.id), {
        onSuccess: () => {
            hideLoading();
            toast.add({ 
                severity: 'success', 
                summary: $t('Success'), 
                detail: $t('Project deleted successfully'), 
                life: 3000 
            });
        },
        onError: () => {
            hideLoading();
            toast.add({ 
                severity: 'error', 
                summary: $t('Error'), 
                detail: $t('Failed to delete project'), 
                life: 3000 
            });
        }
    });
};
</script>

<template>
    <Toast />
    
    <!-- Page Header -->
    <div class="surface-0 border-surface-200 border rounded-lg p-6 mb-4">
        <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
                <div class="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-lg">
                    <i class="pi pi-pencil text-primary-600 text-2xl"></i>
                </div>
                <div>
                    <h1 class="text-2xl font-bold text-surface-900">{{ $t('Edit Project') }}</h1>
                    <p class="text-sm text-surface-600">{{ $t('Update the project information below') }}</p>
                </div>
            </div>
            <Button 
                :label="$t('Delete Project')" 
                icon="pi pi-trash"
                severity="danger"
                text
                @click="confirmDelete"
                :disabled="form.processing"
            />
        </div>
    </div>

    <!-- Form Card -->
    <Card>
        <template #content>
            <form @submit.prevent="submit" class="flex flex-col gap-6">
                <!-- Project Name -->
                <div class="flex flex-col gap-2">
                    <label for="name" class="font-semibold text-surface-900">
                        {{ $t('Project name') }} <span class="text-red-500">*</span>
                    </label>
                    <InputText 
                        id="name"
                        v-model="form.name" 
                        :placeholder="$t('Enter project name')"
                        class="w-full"
                        :disabled="form.processing"
                        :class="{ 'p-invalid': form.errors.name }"
                    />
                    <small v-if="form.errors.name" class="p-error">{{ form.errors.name }}</small>
                </div>

                <!-- Project Color -->
                <div class="flex flex-col gap-2">
                    <label for="color" class="font-semibold text-surface-900">
                        {{ $t('Color') }} <span class="text-red-500">*</span>
                    </label>
                    <div class="flex items-center gap-4">
                        <ColorPicker 
                            id="color"
                            v-model="selectedColor"
                            @update:modelValue="updateColor"
                            :disabled="form.processing"
                            format="hex"
                        />
                        <div class="flex items-center gap-4">
                            <div>
                                <span class="text-sm text-surface-600 block mb-2">{{ $t('Current') }}:</span>
                                <div 
                                    class="w-16 h-16 rounded-lg border-2 border-surface-300 flex items-center justify-center text-white font-semibold"
                                    :style="{ backgroundColor: project.color }"
                                >
                                    {{ project.name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2) }}
                                </div>
                            </div>
                            <i class="pi pi-arrow-right text-surface-400"></i>
                            <div>
                                <span class="text-sm text-surface-600 block mb-2">{{ $t('New') }}:</span>
                                <div 
                                    class="w-16 h-16 rounded-lg border-2 border-surface-300 flex items-center justify-center text-white font-semibold"
                                    :style="{ backgroundColor: form.color }"
                                >
                                    {{ form.name ? form.name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2) : 'PR' }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <small v-if="form.errors.color" class="p-error">{{ form.errors.color }}</small>
                </div>

                <!-- Actions -->
                <div class="flex items-center justify-between pt-4 border-t border-surface-200">
                    <Button 
                        type="button"
                        :label="$t('Reset Changes')" 
                        icon="pi pi-refresh"
                        text 
                        @click="resetForm"
                        :disabled="form.processing"
                    />
                    <div class="flex items-center gap-2">
                        <Button 
                            type="button"
                            :label="$t('Cancel')" 
                            severity="secondary"
                            @click="cancel"
                            :disabled="form.processing"
                        />
                        <Button 
                            type="submit"
                            :label="$t('Update Project')" 
                            icon="pi pi-check"
                            :loading="form.processing"
                            severity="primary"
                        />
                    </div>
                </div>
            </form>
        </template>
    </Card>

    <!-- Project Information -->
    <Card class="mt-4">
        <template #content>
            <div class="flex items-start gap-3">
                <i class="pi pi-info-circle text-primary-600 text-xl mt-1"></i>
                <div class="flex-1">
                    <h3 class="font-semibold text-surface-900 mb-3">{{ $t('Project Information') }}</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <span class="text-sm text-surface-600">{{ $t('Created at') }}:</span>
                            <p class="font-medium text-surface-900">{{ new Date(project.created_at).toLocaleString() }}</p>
                        </div>
                        <div>
                            <span class="text-sm text-surface-600">{{ $t('Last updated') }}:</span>
                            <p class="font-medium text-surface-900">{{ new Date(project.updated_at).toLocaleString() }}</p>
                        </div>
                        <div>
                            <span class="text-sm text-surface-600">{{ $t('Total websites') }}:</span>
                            <p class="font-medium text-surface-900">
                                {{ project.sites_count }} {{ project.sites_count === 1 ? $t('website') : $t('websites') }}
                            </p>
                        </div>
                        <div>
                            <span class="text-sm text-surface-600">{{ $t('Project ID') }}:</span>
                            <p class="font-medium text-surface-900">#{{ project.id }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </Card>
</template>